import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import apiFetch from "../utils/apiFetch";
import UserContext from "../hooks/UserContext";

export default function SubstationCreate() {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const isGlobal = user?.is_global_access;

  const [form, setForm] = useState({
    name: "",
    address: "",
    voltage: "",
    power_mva: "",
    incoming_breaker_6kv_allowed_nominal_current_a: "",
    incoming_breaker_6kv_max_load_current_a: "",
    incoming_breaker_10kv_allowed_nominal_current_a: "",
    incoming_breaker_10kv_max_load_current_a: "",
    outgoing_breaker_6kv_allowed_nominal_current_a: "",
    outgoing_breaker_6kv_max_load_current_a: "",
    outgoing_breaker_10kv_allowed_nominal_current_a: "",
    outgoing_breaker_10kv_max_load_current_a: "",
    switchgear_expansion_6kv_cells: "",
    switchgear_expansion_10kv_cells: "",
    switchgear_expansion_35kv_cells: "",
    switchgear_expansion_110kv_cells: "",
    switchgear_expansion_220kv_cells: "",
    start_year: "",
    longitude: "",
    latitude: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [cities, setCities] = useState([]);
  const [cityId, setCityId] = useState("");
  const [cityLoading, setCityLoading] = useState(false);
  const [cityError, setCityError] = useState("");

  useEffect(() => {
    if (!isGlobal) return;
    let alive = true;

    async function loadCities() {
      setCityLoading(true);
      setCityError("");
      try {
        const res = await apiFetch("/api/city");
        if (!res.ok) throw new Error("CITY_LOAD_FAILED");
        const data = await res.json();
        if (alive) setCities(data);
      } catch (e) {
        console.error(e);
        if (alive) setCityError("Филиаллар рўйхати юкланмади.");
      } finally {
        if (alive) setCityLoading(false);
      }
    }

    loadCities();
    return () => {
      alive = false;
    };
  }, [isGlobal]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) return setError("Номи киритилиши шарт");
    if (isGlobal && !cityId) return setError("Филиални танланг");
    setSaving(true);
    setError("");
    try {
      const body = { ...form };
      if (isGlobal) body.cityId = Number(cityId);
      const res = await apiFetch("/api/substation-management", { method: "POST", body });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.message);
      }
      const sub = await res.json();
      navigate(`/substations/${sub.id}`);
    } catch (err) {
      setError(err.message || "Хатолик юз берди");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Layout>
      <div className="sm-page sm-page--create sm-page--substation-create">
        <section className="sm-create-hero">
          <div>
            <Link to="/substations" className="sm-page__back">
              &larr; Орқага
            </Link>
            <p className="sm-management-hero__eyebrow">Яратиш</p>
            <h1 className="sm-management-hero__title">Янги подстанция</h1>
            <p className="sm-management-hero__text">
              Паспорт ва бошқарув реестри учун асосий техник маълумотлар.
            </p>
          </div>
          <div className="sm-create-hero__chips">
            <span>Номи шарт</span>
            {isGlobal && <span>Филиал шарт</span>}
          </div>
        </section>

        {error && <div className="sm-notice sm-notice--error">{error}</div>}
        {cityError && <div className="sm-notice sm-notice--info">{cityError}</div>}

        <form className="sm-create-layout" onSubmit={handleSubmit}>
          <div className="sm-create-main">
            <section className="sm-create-section">
              <div className="sm-create-section__head">
                <span>01</span>
                <div>
                  <h2>Объект</h2>
                  <p>Подстанция номи ва филиал боғланиши.</p>
                </div>
              </div>
              <div className="sm-create-grid">
                <div className="sm-field sm-form__full">
                  <label className="sm-field__label">Номи *</label>
                  <input
                    className="sm-field__input"
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Подстанция номи"
                    disabled={saving}
                  />
                </div>

                {isGlobal && (
                  <div className="sm-field sm-form__full">
                    <label className="sm-field__label">Филиал *</label>
                    <select
                      className="sm-field__select"
                      value={cityId}
                      onChange={(e) => setCityId(e.target.value)}
                      disabled={saving || cityLoading}
                    >
                      <option value="">{cityLoading ? "Юкланмоқда..." : "Танланг..."}</option>
                      {cities.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </section>

            <section className="sm-create-section">
              <div className="sm-create-section__head">
                <span>02</span>
                <div>
                  <h2>Техник параметрлар</h2>
                  <p>Кучланиш, қувват ва ишга тушган йили.</p>
                </div>
              </div>
              <div className="sm-create-grid">
                <div className="sm-field">
                  <label className="sm-field__label">Кучланиш (кВ)</label>
                  <input
                    className="sm-field__input"
                    value={form.voltage}
                    onChange={set("voltage")}
                    placeholder="500/220/110/35"
                    disabled={saving}
                  />
                </div>
                <div className="sm-field">
                  <label className="sm-field__label">Қувват (МВА)</label>
                  <input
                    className="sm-field__input"
                    value={form.power_mva}
                    onChange={set("power_mva")}
                    placeholder="160"
                    disabled={saving}
                  />
                </div>
                <div className="sm-field">
                  <label className="sm-field__label">Ишга тушган йили</label>
                  <input
                    className="sm-field__input"
                    value={form.start_year}
                    onChange={set("start_year")}
                    placeholder="1985"
                    disabled={saving}
                  />
                </div>
              </div>
            </section>

            <section className="sm-create-section">
              <div className="sm-create-section__head">
                <span>03</span>
                <div>
                  <h2>6/10 кВ кириш ўчиргичлари</h2>
                  <p>Кириш ўчиргичларининг рухсат этилган ва факт юклама токлари.</p>
                </div>
              </div>
              <div className="sm-create-grid">
                <div className="sm-field">
                  <label className="sm-field__label">6 кВ кириш ўчиргичи: рухсат этилган номинал токи (A)</label>
                  <input className="sm-field__input" type="number" min="0" step="any" inputMode="decimal" value={form.incoming_breaker_6kv_allowed_nominal_current_a} onChange={set("incoming_breaker_6kv_allowed_nominal_current_a")} placeholder="630" disabled={saving} />
                </div>
                <div className="sm-field">
                  <label className="sm-field__label">6 кВ кириш ўчиргичи: максимал юклама токи, факт бўйича (A)</label>
                  <input className="sm-field__input" type="number" min="0" step="any" inputMode="decimal" value={form.incoming_breaker_6kv_max_load_current_a} onChange={set("incoming_breaker_6kv_max_load_current_a")} placeholder="420" disabled={saving} />
                </div>
                <div className="sm-field">
                  <label className="sm-field__label">10 кВ кириш ўчиргичи: рухсат этилган номинал токи (A)</label>
                  <input className="sm-field__input" type="number" min="0" step="any" inputMode="decimal" value={form.incoming_breaker_10kv_allowed_nominal_current_a} onChange={set("incoming_breaker_10kv_allowed_nominal_current_a")} placeholder="1000" disabled={saving} />
                </div>
                <div className="sm-field">
                  <label className="sm-field__label">10 кВ кириш ўчиргичи: максимал юклама токи, факт бўйича (A)</label>
                  <input className="sm-field__input" type="number" min="0" step="any" inputMode="decimal" value={form.incoming_breaker_10kv_max_load_current_a} onChange={set("incoming_breaker_10kv_max_load_current_a")} placeholder="780" disabled={saving} />
                </div>
              </div>
            </section>

            <section className="sm-create-section">
              <div className="sm-create-section__head">
                <span>04</span>
                <div>
                  <h2>6/10 кВ чиқиш ўчиргичлари</h2>
                  <p>Чиқиш ўчиргичларининг рухсат этилган ва факт юклама токлари.</p>
                </div>
              </div>
              <div className="sm-create-grid">
                <div className="sm-field">
                  <label className="sm-field__label">6 кВ чиқиш ўчиргичи: рухсат этилган номинал токи (A)</label>
                  <input className="sm-field__input" type="number" min="0" step="any" inputMode="decimal" value={form.outgoing_breaker_6kv_allowed_nominal_current_a} onChange={set("outgoing_breaker_6kv_allowed_nominal_current_a")} placeholder="100" disabled={saving} />
                </div>
                <div className="sm-field">
                  <label className="sm-field__label">6 кВ чиқиш ўчиргичи: максимал юклама токи, факт бўйича (A)</label>
                  <input className="sm-field__input" type="number" min="0" step="any" inputMode="decimal" value={form.outgoing_breaker_6kv_max_load_current_a} onChange={set("outgoing_breaker_6kv_max_load_current_a")} placeholder="300" disabled={saving} />
                </div>
                <div className="sm-field">
                  <label className="sm-field__label">10 кВ чиқиш ўчиргичи: рухсат этилган номинал токи (A)</label>
                  <input className="sm-field__input" type="number" min="0" step="any" inputMode="decimal" value={form.outgoing_breaker_10kv_allowed_nominal_current_a} onChange={set("outgoing_breaker_10kv_allowed_nominal_current_a")} placeholder="100" disabled={saving} />
                </div>
                <div className="sm-field">
                  <label className="sm-field__label">10 кВ чиқиш ўчиргичи: максимал юклама токи, факт бўйича (A)</label>
                  <input className="sm-field__input" type="number" min="0" step="any" inputMode="decimal" value={form.outgoing_breaker_10kv_max_load_current_a} onChange={set("outgoing_breaker_10kv_max_load_current_a")} placeholder="300" disabled={saving} />
                </div>
              </div>
            </section>

            <section className="sm-create-section">
              <div className="sm-create-section__head">
                <span>05</span>
                <div>
                  <h2>ТҚ кенгайтириш имконияти</h2>
                  <p>Кенгайтириш мумкин бўлган ячейкалар сони; бўш қиймат номаълум, 0 эса имконият йўқ дегани.</p>
                </div>
              </div>
              <div className="sm-create-grid">
                <div className="sm-field">
                  <label className="sm-field__label">6 кВ ТҚ: кенгайтириш мумкин бўлган ячейкалар сони</label>
                  <input className="sm-field__input" type="number" min="0" step="1" inputMode="numeric" value={form.switchgear_expansion_6kv_cells} onChange={set("switchgear_expansion_6kv_cells")} placeholder="2" disabled={saving} />
                </div>
                <div className="sm-field">
                  <label className="sm-field__label">10 кВ ТҚ: кенгайтириш мумкин бўлган ячейкалар сони</label>
                  <input className="sm-field__input" type="number" min="0" step="1" inputMode="numeric" value={form.switchgear_expansion_10kv_cells} onChange={set("switchgear_expansion_10kv_cells")} placeholder="2" disabled={saving} />
                </div>
                <div className="sm-field">
                  <label className="sm-field__label">35 кВ ТҚ: кенгайтириш мумкин бўлган ячейкалар сони</label>
                  <input className="sm-field__input" type="number" min="0" step="1" inputMode="numeric" value={form.switchgear_expansion_35kv_cells} onChange={set("switchgear_expansion_35kv_cells")} placeholder="1" disabled={saving} />
                </div>
                <div className="sm-field">
                  <label className="sm-field__label">110 кВ ТҚ: кенгайтириш мумкин бўлган ячейкалар сони</label>
                  <input className="sm-field__input" type="number" min="0" step="1" inputMode="numeric" value={form.switchgear_expansion_110kv_cells} onChange={set("switchgear_expansion_110kv_cells")} placeholder="1" disabled={saving} />
                </div>
                <div className="sm-field">
                  <label className="sm-field__label">220 кВ ТҚ: кенгайтириш мумкин бўлган ячейкалар сони</label>
                  <input className="sm-field__input" type="number" min="0" step="1" inputMode="numeric" value={form.switchgear_expansion_220kv_cells} onChange={set("switchgear_expansion_220kv_cells")} placeholder="0" disabled={saving} />
                </div>
              </div>
            </section>

            <section className="sm-create-section">
              <div className="sm-create-section__head">
                <span>06</span>
                <div>
                  <h2>Жойлашув</h2>
                  <p>Манзил ва координаталар.</p>
                </div>
              </div>
              <div className="sm-create-grid">
                <div className="sm-field sm-form__full">
                  <label className="sm-field__label">Манзил</label>
                  <input
                    className="sm-field__input"
                    value={form.address}
                    onChange={set("address")}
                    placeholder="Шаҳар, кўча..."
                    disabled={saving}
                  />
                </div>
                <div className="sm-field">
                  <label className="sm-field__label">Кенглик (latitude)</label>
                  <input
                    className="sm-field__input"
                    value={form.latitude}
                    onChange={set("latitude")}
                    placeholder="41.311"
                    disabled={saving}
                  />
                </div>
                <div className="sm-field">
                  <label className="sm-field__label">Узунлик (longitude)</label>
                  <input
                    className="sm-field__input"
                    value={form.longitude}
                    onChange={set("longitude")}
                    placeholder="69.279"
                    disabled={saving}
                  />
                </div>
              </div>
            </section>
          </div>

          <aside className="sm-create-sidebar">
            <div className="sm-create-checklist">
              <h2>Ҳолат</h2>
              <span className={form.name.trim() ? "is-done" : ""}>Номи</span>
              {isGlobal && <span className={cityId ? "is-done" : ""}>Филиал</span>}
              <span className={form.voltage ? "is-done" : ""}>Кучланиш</span>
              <span className={form.latitude && form.longitude ? "is-done" : ""}>Координата</span>
            </div>

            <div className="sm-create-actions">
              <button type="button" className="sm-btn sm-btn--ghost" onClick={() => navigate("/substations")} disabled={saving}>
                Бекор қилиш
              </button>
              <button type="submit" className="sm-btn sm-btn--primary" disabled={saving}>
                {saving ? "Сақланмоқда..." : "Яратиш"}
              </button>
            </div>
          </aside>
        </form>
      </div>
    </Layout>
  );
}
